import { createFileRoute, useNavigate } from '@tanstack/react-router';
import dayjs from 'dayjs';
import { useCallback, useMemo } from 'react';
import z from 'zod';
import {
  CheckInConsole,
  type CheckInEvent,
  CheckInEventCard,
  CheckInIntro,
  CheckInNoEvents,
  type CheckInSoldier,
  GateOpenPill,
} from '@/components/ui/admin/check-in';
import { Content } from '@/components/ui/content';
import { date } from '@/lib/date';
import { meta } from '@/lib/meta';
import { usePermissions } from '@/services/auth';
import { useCheckIn, useCheckInConsole, useCheckInEvents } from '@/services/check-in';

/**
 * Check-in no portão (módulo 07, prints 10–13 do `aliste-se-admin-kit`). A operação aberta fica
 * na URL (`?operacao=<id>`), não num segmento: `/check-in/<id>` viraria permissão de `update`
 * no `AppLayout`.
 */
export const Route = createFileRoute('/_app/check-in')({
  head: () => meta({ label: 'Check-in' }),
  validateSearch: z.object({ operacao: z.string().optional() }),
  component: RouteComponent,
});

const COVER_URL = '/assets/military-bg.jpg';

const SEQUENCE_LABELS = { forja: 'Forja', kids: 'Kids' } as const;

/** Placeholder do fonte (`/user.jpg`) não é foto de verdade — mostra as iniciais. */
const realPhoto = (photo: string | null) =>
  photo && photo !== '/user.jpg' ? photo : null;

const KIND_LABELS = { forja: 'Forja', kids: 'Kids', outro: 'Outro' } as const;

/** `events.date` vem em UTC sem fuso ("2026-12-11 03:00:00"): lê como UTC e mostra em Brasília. */
const eventDate = (value: string) => dayjs.utc(value).tz('America/Sao_Paulo');

function RouteComponent() {
  const { operacao } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const open = (id?: string) => navigate({ search: id ? { operacao: id } : {} });

  return operacao ? (
    <ConsoleStep eventId={operacao} onBack={() => open()} />
  ) : (
    <EventsStep onOpen={open} />
  );
}

function EventsStep({ onOpen }: { onOpen: (id: string) => void }) {
  const { data, isLoading } = useCheckInEvents();

  const events: CheckInEvent[] = (data ?? []).map((e) => ({
    id: e.id,
    kind: KIND_LABELS[e.kind],
    name: e.name,
    dateLabel: e.period,
    city: e.city,
    live: e.live,
    total: e.total,
    done: e.done,
  }));

  return (
    <Content
      isLoading={isLoading}
      breadcrumbs={{
        title: 'Check-in',
        items: [{ label: 'Operação' }, { label: 'Check-in', active: true }],
        action: <GateOpenPill />,
      }}
    >
      <div className="flex w-full max-w-[1480px] flex-col gap-6">
        <CheckInIntro />
        {!isLoading && events.length === 0 ? (
          <CheckInNoEvents />
        ) : (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(min(100%,360px),1fr))] gap-5">
            {events.map((e) => (
              <CheckInEventCard
                key={e.id}
                event={e}
                coverUrl={COVER_URL}
                onOpen={() => onOpen(e.id)}
              />
            ))}
          </div>
        )}
      </div>
    </Content>
  );
}

function ConsoleStep({ eventId, onBack }: { eventId: string; onBack: () => void }) {
  const { data, isLoading } = useCheckInConsole(eventId);
  const { mutateAsync: checkIn } = useCheckIn();
  const { createPermissions } = usePermissions();
  const { canCreate } = createPermissions('check-in');

  const event = data?.event;

  const soldiers: CheckInSoldier[] = useMemo(
    () =>
      (data?.soldiers ?? []).map((s) => ({
        id: s.id,
        name: s.name.trim(),
        age: s.age,
        city: s.city,
        photo: realPhoto(s.photo),
        paid: !!s.paid,
        soldierNumber: s.soldierNumber,
        checkedAt: s.checkedInAt ? date(s.checkedInAt).format('HH:mm') : null,
      })),
    [data],
  );

  // Hora do 1º check-in desta operação ("N desde 19:02") e o recebido no portão.
  const { since, gateCash } = useMemo(() => {
    const list = data?.soldiers ?? [];
    const first = list
      .map((s) => s.checkedInAt)
      .filter((v): v is string => !!v)
      .sort()[0];
    const paidAtGate = list.filter((s) => s.paidAtCheckIn).length;

    return {
      since: first ? date(first).format('HH:mm') : null,
      gateCash: paidAtGate * (data?.event.value ?? 0),
    };
  }, [data]);

  const handleCheckIn = useCallback(
    async (s: CheckInSoldier) => {
      try {
        const updated = await checkIn(s.id);
        return {
          name: s.name,
          number: updated.soldierNumber,
          paidAtGate: updated.paidAtCheckIn,
        };
      } catch {
        return null; // toast e recarga da lista ficam no `useCheckIn`
      }
    },
    [checkIn],
  );

  const sequence = event ? SEQUENCE_LABELS[event.sequence] : '';

  return (
    <Content
      isLoading={isLoading}
      breadcrumbs={{
        title: event?.name ?? 'Check-in',
        items: [
          { label: 'Operação' },
          { label: 'Check-in', onClick: onBack },
          ...(event
            ? [
                {
                  label: eventDate(event.date).format('DD MMM YYYY').replace('.', ''),
                  active: true,
                },
              ]
            : []),
        ],
        action: <GateOpenPill />,
        onBack,
      }}
    >
      {event && (
        <CheckInConsole
          key={event.id}
          sequence={sequence}
          soldiers={soldiers}
          nextNumber={data.nextNumber}
          price={event.value}
          coverUrl={COVER_URL}
          since={since}
          gateCash={gateCash}
          canCheckIn={canCreate}
          onCheckIn={handleCheckIn}
        />
      )}
    </Content>
  );
}
